import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { getClientes, type ClienteRegistro } from '../lib/clientesStorage';
import { fetchClientesFromApi } from '../lib/clientesApi';

export default function ClientesPage() {
  const [clientes, setClientes] = useState<ClienteRegistro[]>([]);
  const [busca, setBusca] = useState('');
  const [expandidoId, setExpandidoId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const refresh = useCallback(async () => {
    const locais = getClientes();
    setClientes(locais);
    setCarregando(true);
    try {
      const daApi = await fetchClientesFromApi();
      if (daApi.length > 0) {
        const porEmail = new Map(locais.map((c) => [(c.email || '').toLowerCase(), c]));
        daApi.forEach((c) => porEmail.set((c.email || '').toLowerCase(), c));
        setClientes(Array.from(porEmail.values()).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useRefetchWhenCatalogChanges(() => {
    void refresh();
  });

  const clientesFiltrados = clientes.filter((c) => {
    if (!busca.trim()) return true;
    const q = busca.trim().toLowerCase();
    const nomeCompleto = `${c.nome || ''} ${c.sobrenome || ''}`.trim().toLowerCase();
    return (
      nomeCompleto.includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.apelido || '').toLowerCase().includes(q) ||
      (c.telefone || '').replace(/\D/g, '').includes(q.replace(/\D/g, '')) ||
      (c.numeroDocumento || '').replace(/\D/g, '').includes(q.replace(/\D/g, ''))
    );
  });

  const formatDate = (dateInput: string) => {
    try {
      const d = new Date(dateInput);
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
    } catch {
      return '—';
    }
  };

  return (
    <AdminLayout>
      <div className="w-full">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <span className="text-gray-800">Clientes</span>
          </nav>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-600 mt-1">Cadastros e dados de perfil dos clientes do app</p>
          {carregando && <span className="text-xs text-gray-500 ml-2">Carregando da API...</span>}
        </div>

        <div className="px-4 sm:px-6 py-6">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Buscar por nome, email, apelido, telefone ou documento"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="py-2 px-3 rounded-lg border border-gray-300 w-full max-w-md"
            />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="bg-orange-500 text-white">
                    <th className="text-left py-3 px-4 font-semibold">Nome</th>
                    <th className="text-left py-3 px-4 font-semibold">Sobrenome</th>
                    <th className="text-left py-3 px-4 font-semibold">Apelido</th>
                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                    <th className="text-left py-3 px-4 font-semibold">Telefone</th>
                    <th className="text-left py-3 px-4 font-semibold">Documento</th>
                    <th className="text-left py-3 px-4 font-semibold">Cadastro</th>
                    <th className="text-left py-3 px-4 font-semibold">Origem</th>
                    <th className="text-left py-3 px-4 font-semibold w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {clientesFiltrados.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{c.nome || '—'}</td>
                        <td className="py-3 px-4">{c.sobrenome || '—'}</td>
                        <td className="py-3 px-4">{c.apelido || '—'}</td>
                        <td className="py-3 px-4">{c.email || '—'}</td>
                        <td className="py-3 px-4">{c.telefone || '—'}</td>
                        <td className="py-3 px-4">
                          {c.tipoDocumento && c.numeroDocumento
                            ? `${c.tipoDocumento}: ${c.numeroDocumento}`
                            : c.numeroDocumento || '—'}
                        </td>
                        <td className="py-3 px-4">{formatDate(c.createdAt)}</td>
                        <td className="py-3 px-4">{c.provider === 'google' ? 'Google' : 'Email'}</td>
                        <td className="py-3 px-4">
                          <button
                            type="button"
                            onClick={() => setExpandidoId((id) => (id === c.id ? null : c.id))}
                            className="p-1.5 text-gray-500 hover:text-orange-600 rounded hover:bg-orange-50"
                            title={expandidoId === c.id ? 'Recolher' : 'Ver mais'}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${expandidoId === c.id ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                      {expandidoId === c.id && (
                        <tr key={`${c.id}-expand`} className="bg-gray-50 border-b border-gray-100">
                          <td colSpan={9} className="py-4 px-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">País de origem:</span>{' '}
                                {c.paisOrigem || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Estado:</span> {c.estado || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Cidade:</span> {c.cidade || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">CEP:</span> {c.cep || '—'}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="text-gray-500">Endereço:</span> {c.endereco || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Sexo:</span> {c.sexo || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Data nasc.:</span>{' '}
                                {c.dataNascimento ? formatDate(c.dataNascimento) : '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Chave PIX:</span> {c.chavePix || '—'}
                              </div>
                              <div>
                                <span className="text-gray-500">Atualizado:</span>{' '}
                                {formatDate(c.updatedAt)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {clientesFiltrados.length === 0 && (
              <div className="p-8 text-center text-gray-500">Nenhum cliente cadastrado.</div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
